<template>
  <v-dialog max-width="560px" v-model="showDialog" persistent>
    <v-card>
      <v-card-title class="secondary white--text py-3 px-5">
        <v-icon color="white" class="mr-2">mdi-flask-outline</v-icon>
        <span class="text-subtitle-1 font-weight-bold">
          Simulate Gate Event — {{ flow === "vendor" ? "Vendor Flow" : "Internal Flow" }}
        </span>
        <v-spacer />
        <v-btn icon color="white" small @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-6 pt-5 pb-2">
        <v-btn-toggle
          v-model="form.event_type"
          mandatory
          dense
          class="mb-4"
          :disabled="!!prefillContainer"
        >
          <v-btn value="gate_out" small>Gate Out</v-btn>
          <v-btn value="gate_in" small>Gate In</v-btn>
        </v-btn-toggle>

        <v-row dense>
          <v-col cols="6" v-if="flow === 'vendor'">
            <Autocomplete
              label="Vendor *"
              :items="vendorList"
              itemText="company_name"
              itemValue="company_name"
              :loading="vendorLoading"
              :totalItems="vendorCount"
              @updateList="fetchVendors"
              hide-details="auto"
              v-model="form.vendor_name"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              label="Container Number *"
              outlined
              dense
              hide-details="auto"
              v-model="form.container_number"
            />
          </v-col>
          <v-col cols="6">
            <MultiSelect
              label="Container Type"
              :items="containerTypeList"
              itemText="type_name"
              itemValue="type_name"
              outlined
              dense
              clearable
              hide-details="auto"
              v-model="form.container_type"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              label="Origin"
              outlined
              dense
              hide-details="auto"
              v-model="form.origin"
            />
          </v-col>
          <v-col cols="6">
            <v-select
              label="Destination"
              :items="destinationOptions"
              outlined
              dense
              clearable
              hide-details="auto"
              v-model="form.destination"
            />
          </v-col>
          <v-col cols="6">
            <Autocomplete
              label="Driver"
              :items="driverList"
              itemText="license_number"
              itemValue="license_number"
              :loading="driverLoading"
              :totalItems="driverCount"
              @updateList="fetchDrivers"
              hide-details="auto"
              v-model="form.driver_id"
            />
          </v-col>
          <v-col cols="6">
            <Autocomplete
              label="Vehicle"
              :items="vehicleList"
              itemText="vehicle_number"
              itemValue="vehicle_number"
              :loading="vehicleLoading"
              :totalItems="vehicleCount"
              @updateList="fetchVehicles"
              hide-details="auto"
              v-model="form.vehicle_no"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
              label="VBS Reference No"
              outlined
              dense
              hide-details="auto"
              v-model="form.vbs_reference_no"
            />
          </v-col>
          <v-col cols="6" v-if="form.event_type === 'gate_in'">
            <v-text-field
              label="Voyage ID (leave blank if no prior gate-out)"
              outlined
              dense
              hide-details="auto"
              v-model="form.voyage_id"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider class="mt-3" />

      <v-card-actions class="px-5 py-3">
        <v-spacer />
        <v-btn depressed outlined @click="closeDialog" class="mr-2">Cancel</v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="submitting"
          :disabled="!form.container_number || (flow === 'vendor' && !form.vendor_name)"
          min-width="120"
          @click="submit"
        >
          <v-icon small class="mr-1">mdi-send</v-icon>
          Simulate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Autocomplete from "@/components/FormBaseComponents/Autocomplete.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";

export default {
  name: "SimulateGateEventDialog",
  components: { Autocomplete, MultiSelect },
  props: {
    value: { type: Boolean, default: false },
    prefillContainer: { type: Object, default: null },
    flow: { type: String, default: "internal" },
  },
  watch: {
    value(val) {
      if (val) {
        this.resetForm();
        this.fetchInitialData().then(() => {
          if (this.prefillContainer) {
            this.prefillFromContainer();
          } else if (this.flow === "vendor") {
            this.autoFillVendor();
          } else {
            this.autoFill();
          }
        });
      }
    },
  },
  data() {
    return {
      destinationOptions: ["SIC", "SCT", "SICD"],
      form: {
        event_type: "gate_out",
        vendor_name: null,
        container_number: "",
        container_type: null,
        origin: "KCT",
        destination: null,
        driver_id: null,
        vehicle_no: null,
        vbs_reference_no: "",
        voyage_id: "",
      },
      containerTypeList: [],
      driverList: [],
      driverCount: 0,
      driverLoading: false,
      vehicleList: [],
      vehicleCount: 0,
      vehicleLoading: false,
      vendorList: [],
      vendorCount: 0,
      vendorLoading: false,
      submitting: false,
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  methods: {
    resetForm() {
      this.form = {
        event_type: "gate_out",
        vendor_name: null,
        container_number: "",
        container_type: null,
        origin: "KCT",
        destination: null,
        driver_id: null,
        vehicle_no: null,
        vbs_reference_no: "",
        voyage_id: "",
      };
    },
    closeDialog() {
      this.showDialog = false;
    },
    fetchInitialData() {
      return Promise.all([
        this.$api.containerType.getContainerTypeList({ limit: "all" }).then((res) => {
          this.containerTypeList = res.data;
        }),
        this.fetchDrivers({ limit: 10, offset: 0 }),
        this.fetchVehicles({ limit: 10, offset: 0 }),
        this.fetchVendors({ limit: 10, offset: 0 }),
      ]);
    },
    fetchVendors(params = {}) {
      this.vendorLoading = true;
      return this.$api.company
        .getCompanyList({ company_type: "Transporter", limit: 10, ...params })
        .then((res) => {
          this.vendorList = params.offset ? [...this.vendorList, ...res.data] : res.data;
          this.vendorCount = res.count;
        })
        .finally(() => {
          this.vendorLoading = false;
        });
    },
    fetchDrivers(params = {}) {
      this.driverLoading = true;
      return this.$api.driver
        .getDriverSelectList({ limit: 10, ...params })
        .then((res) => {
          this.driverList = params.offset ? [...this.driverList, ...res.data] : res.data;
          this.driverCount = res.count;
        })
        .finally(() => {
          this.driverLoading = false;
        });
    },
    fetchVehicles(params = {}) {
      this.vehicleLoading = true;
      return this.$api.vehicle
        .getVehicleSelectList({ limit: 10, ...params })
        .then((res) => {
          this.vehicleList = params.offset ? [...this.vehicleList, ...res.data] : res.data;
          this.vehicleCount = res.count;
        })
        .finally(() => {
          this.vehicleLoading = false;
        });
    },
    randomInt(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },
    randomAlphaNum(length) {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let out = "";
      for (let i = 0; i < length; i++) {
        out += chars[this.randomInt(0, chars.length - 1)];
      }
      return out;
    },
    randomItem(list) {
      return list && list.length ? list[this.randomInt(0, list.length - 1)] : null;
    },
    autoFill() {
      this.form.container_number = `SIM${this.randomAlphaNum(7)}`;
      this.form.container_type = this.randomItem(["20 Feet", "40 Feet"]);
      this.form.destination = this.randomItem(this.destinationOptions);
      this.form.driver_id = this.randomItem(
        this.driverList.map((d) => d.license_number).filter(Boolean)
      );
      this.form.vehicle_no = this.randomItem(
        this.vehicleList.map((v) => v.vehicle_number).filter(Boolean)
      );
      this.form.vbs_reference_no = `VBS${this.randomInt(100000, 999999)}`;
    },
    autoFillVendor() {
      this.autoFill();
      this.form.vendor_name = this.randomItem(
        this.vendorList.map((v) => v.company_name).filter(Boolean)
      );
    },
    prefillFromContainer() {
      const c = this.prefillContainer;
      this.form.event_type = "gate_in";
      this.form.container_number = c.container_number || "";
      this.form.voyage_id = c.voyage_id || "";
      this.form.container_type = c.container_type_name || c.container_type || null;
      this.form.origin = c.origin_name || this.form.origin;
      this.form.destination = this.destinationOptions.includes(c.destination_name)
        ? c.destination_name
        : this.destinationOptions[0];
      this.form.driver_id = c.driver_id_raw || null;
      this.form.vehicle_no = c.vehicle_no || null;
      this.form.vbs_reference_no = c.vbs_reference_no || "";
      if (this.flow === "vendor") {
        this.form.vendor_name = c.vendor_name || null;
      }
    },
    submit() {
      this.submitting = true;
      const getVal = (v, key) => (v && typeof v === "object" ? v[key] : v);
      const eventData = {
        container_number: this.form.container_number,
        container_type: this.form.container_type,
        origin: this.form.origin,
        destination: this.form.destination,
        driver_id: getVal(this.form.driver_id, "license_number"),
        vehicle_no: getVal(this.form.vehicle_no, "vehicle_number"),
        vbs_reference_no: this.form.vbs_reference_no,
        timestamp: new Date().toISOString(),
      };
      if (this.form.event_type === "gate_in" && this.form.voyage_id) {
        eventData.voyage_id = this.form.voyage_id;
      }
      if (this.flow === "vendor" && this.form.vendor_name) {
        eventData.vendor_name = getVal(this.form.vendor_name, "company_name");
      }
      const payload = {
        event_type: this.form.event_type,
        terminal: this.form.event_type === "gate_out" ? this.form.origin : this.form.destination,
        data: [eventData],
      };
      this.$api.emergencyOrderContainer
        .simulateGateEvent(payload)
        .then(() => {
          this.$bus.$emit("showToastMessage", {
            message: "Gate event simulated successfully!",
            color: "success",
          });
          this.$emit("simulated");
          this.closeDialog();
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", {
            message: err?.message || "Failed to simulate gate event.",
            color: "error",
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>
